import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ParchmentCard from '@/components/ui/ParchmentCard';
import ProgressBar from '@/components/ui/ProgressBar';
import Button from '@/components/ui/Button';
import useAuthStore from '@/store/authStore';
import useAuth from '@/hooks/useAuth';
import * as playerService from '@/services/playerService';
import { resolveAvatarUrl } from '@/utils/avatar';
import { resolveAssetUrl } from '@/utils/assets';

const XP_THRESHOLDS = [0, 500, 1200, 2500, 4500, 7500, 12000, 18000, 26000, 36000];

const getXpProgress = (xp, level) => {
  const lvl = Math.max(1, Math.min(level || 1, 10)) - 1;
  const current = XP_THRESHOLDS[lvl] || 0;
  const next = XP_THRESHOLDS[lvl + 1] || current + 500;
  return { current: xp - current, needed: next - current };
};

const AVATAR_PRESETS = [
  '/avatars/avatar_1.png',
  '/characters/pipo-avatar.png',
  '/characters/papagaio.png',
];

function IconPreset() {
  return (
    <svg viewBox="0 0 36 36" fill="none" className="h-8 w-8" aria-hidden>
      <circle cx="18" cy="18" r="16" fill="url(#presetBg)" stroke="#B8860B" strokeWidth="1.5"/>
      <circle cx="18" cy="14" r="5" fill="url(#presetFace)" stroke="#D4AF37" strokeWidth="1"/>
      <path d="M8 30c0-5.5 4.5-9 10-9s10 3.5 10 9" fill="url(#presetBody)" stroke="#D4AF37" strokeWidth="1"/>
      <path d="M13 12c1-2 5-3 7-1" stroke="#FFD700" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
      <circle cx="15.5" cy="14" r="1" fill="#5D4037"/>
      <circle cx="20.5" cy="14" r="1" fill="#5D4037"/>
      <path d="M16 16.5c1 1 3 1 4 0" stroke="#5D4037" strokeWidth="0.8" strokeLinecap="round"/>
      <defs>
        <radialGradient id="presetBg" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#FFE082"/>
          <stop offset="100%" stopColor="#B8860B"/>
        </radialGradient>
        <radialGradient id="presetFace" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#FFCC80"/>
          <stop offset="100%" stopColor="#FF8A65"/>
        </radialGradient>
        <linearGradient id="presetBody" x1="8" y1="21" x2="28" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.4"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

function IconCamera() {
  return (
    <svg viewBox="0 0 36 36" fill="none" className="h-8 w-8" aria-hidden>
      <rect x="2" y="10" width="32" height="22" rx="4" fill="url(#camBody)" stroke="#1B6CA8" strokeWidth="1.5"/>
      <circle cx="18" cy="21" r="7" fill="url(#camLens)" stroke="#3A8FD1" strokeWidth="1.2"/>
      <circle cx="18" cy="21" r="4.5" fill="url(#camInner)"/>
      <circle cx="18" cy="21" r="2" fill="#0D4F7C" opacity="0.5"/>
      <circle cx="20.5" cy="19" r="1" fill="white" opacity="0.6"/>
      <rect x="11" y="4" width="8" height="6" rx="2" fill="#1B6CA8" stroke="#3A8FD1" strokeWidth="1"/>
      <circle cx="29" cy="14" r="2" fill="#3A8FD1" opacity="0.7"/>
      <defs>
        <linearGradient id="camBody" x1="2" y1="10" x2="34" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#3A8FD1" stopOpacity="0.4"/>
          <stop offset="100%" stopColor="#0D4F7C" stopOpacity="0.6"/>
        </linearGradient>
        <radialGradient id="camLens" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#5BA8D4"/>
          <stop offset="100%" stopColor="#1B6CA8"/>
        </radialGradient>
        <radialGradient id="camInner" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#7ECFF0"/>
          <stop offset="100%" stopColor="#0D4F7C"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

function IconAI() {
  return (
    <svg viewBox="0 0 36 36" fill="none" className="h-8 w-8" aria-hidden>
      <circle cx="18" cy="18" r="15" fill="url(#aiBg)" stroke="#7C3AED" strokeWidth="1.5"/>
      <circle cx="18" cy="15" r="6" fill="url(#aiFace)" stroke="#A78BFA" strokeWidth="1"/>
      <path d="M9 31c0-5 4-8.5 9-8.5s9 3.5 9 8.5" fill="url(#aiBody)" stroke="#A78BFA" strokeWidth="1"/>
      <path d="M12 8 L14 5 L16 8" stroke="#C4B5FD" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M20 7 L22 4 L24 7" stroke="#F0ABFC" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="10" cy="6" r="1.2" fill="#F0ABFC" opacity="0.8"/>
      <circle cx="27" cy="9" r="1.5" fill="#C4B5FD" opacity="0.7"/>
      <circle cx="15.5" cy="15" r="1.2" fill="#4C1D95"/>
      <circle cx="20.5" cy="15" r="1.2" fill="#4C1D95"/>
      <path d="M15.5 17.5c1.5 1.5 3.5 1.5 5 0" stroke="#4C1D95" strokeWidth="0.9" strokeLinecap="round"/>
      <path d="M14 12.5c1-1.5 3-2 5-1.5" stroke="#C4B5FD" strokeWidth="0.8" strokeLinecap="round" opacity="0.7"/>
      <defs>
        <radialGradient id="aiBg" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#DDD6FE"/>
          <stop offset="60%" stopColor="#8B5CF6"/>
          <stop offset="100%" stopColor="#4C1D95"/>
        </radialGradient>
        <radialGradient id="aiFace" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#EDE9FE"/>
          <stop offset="100%" stopColor="#C4B5FD"/>
        </radialGradient>
        <linearGradient id="aiBody" x1="9" y1="22.5" x2="27" y2="31" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.5"/>
          <stop offset="100%" stopColor="#4C1D95" stopOpacity="0.4"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

function StatCoinIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" className="mx-auto mb-1 h-7 w-7" aria-hidden>
      <circle cx="14" cy="14" r="12" fill="url(#sCoinOuter)" stroke="#8B6914" strokeWidth="1.2"/>
      <circle cx="14" cy="14" r="8.5" fill="url(#sCoinInner)" stroke="#D4AF37" strokeWidth="0.8"/>
      <circle cx="14" cy="14" r="5" fill="rgba(255,215,0,0.15)"/>
      <text x="14" y="18.5" textAnchor="middle" fontSize="9" fontWeight="900" fill="#7A5200" fontFamily="serif">₢</text>
      <ellipse cx="10" cy="10" rx="3" ry="1.5" fill="white" opacity="0.2" transform="rotate(-30 10 10)"/>
      <defs>
        <radialGradient id="sCoinOuter" cx="35%" cy="28%">
          <stop offset="0%" stopColor="#FFE55C"/>
          <stop offset="55%" stopColor="#D4AF37"/>
          <stop offset="100%" stopColor="#8B6914"/>
        </radialGradient>
        <radialGradient id="sCoinInner" cx="38%" cy="32%">
          <stop offset="0%" stopColor="#FFF176"/>
          <stop offset="100%" stopColor="#C8960C"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

function StatPOIIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" className="mx-auto mb-1 h-7 w-7" aria-hidden>
      <circle cx="14" cy="14" r="12" fill="url(#sPoiBg)" stroke="#1B6CA8" strokeWidth="1.2"/>
      <path d="M14 5C10.1 5 7 8.1 7 12c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7Z"
        fill="url(#sPoiPin)" stroke="#3A8FD1" strokeWidth="1"/>
      <circle cx="14" cy="12" r="3" fill="white" opacity="0.9"/>
      <circle cx="14" cy="12" r="1.5" fill="#1B6CA8"/>
      <defs>
        <radialGradient id="sPoiBg" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#60A5FA"/>
          <stop offset="100%" stopColor="#1B6CA8"/>
        </radialGradient>
        <linearGradient id="sPoiPin" x1="7" y1="5" x2="21" y2="25" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#93C5FD"/>
          <stop offset="100%" stopColor="#2563EB"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

function StatMissionIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" className="mx-auto mb-1 h-7 w-7" aria-hidden>
      <circle cx="14" cy="14" r="12" fill="url(#sMisBg)" stroke="#1A4F1A" strokeWidth="1.2"/>
      <rect x="8" y="6" width="12" height="16" rx="2" fill="url(#sMisPaper)" stroke="#4ADE80" strokeWidth="0.8"/>
      <path d="M10.5 10.5h7M10.5 13h7M10.5 15.5h4.5" stroke="white" strokeWidth="1.1" strokeLinecap="round" opacity="0.9"/>
      <path d="M7 19l2 2 4-4" stroke="#4ADE80" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <defs>
        <radialGradient id="sMisBg" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#4ADE80"/>
          <stop offset="100%" stopColor="#15803D"/>
        </radialGradient>
        <linearGradient id="sMisPaper" x1="8" y1="6" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="rgba(255,255,255,0.95)"/>
          <stop offset="100%" stopColor="rgba(220,252,231,0.85)"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

function StatGemIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" className="mx-auto mb-1 h-7 w-7" aria-hidden>
      <circle cx="14" cy="14" r="12" fill="url(#sGemBg)" stroke="#6D28D9" strokeWidth="1.2"/>
      <path d="M14 5l-6 7h12L14 5Z" fill="url(#sGemTop)" stroke="#A78BFA" strokeWidth="0.8" strokeLinejoin="round"/>
      <path d="M8 12l6 11 6-11H8Z" fill="url(#sGemBot)" stroke="#A78BFA" strokeWidth="0.8" strokeLinejoin="round"/>
      <path d="M8 12h12" stroke="#C4B5FD" strokeWidth="0.7"/>
      <path d="M11 12l3 11M17 12l-3 11" stroke="#7C3AED" strokeWidth="0.5" opacity="0.5"/>
      <path d="M11 12L14 5M17 12L14 5" stroke="#C4B5FD" strokeWidth="0.5" opacity="0.6"/>
      <ellipse cx="12" cy="9" rx="1.5" ry="0.8" fill="white" opacity="0.35" transform="rotate(-20 12 9)"/>
      <defs>
        <radialGradient id="sGemBg" cx="35%" cy="30%">
          <stop offset="0%" stopColor="#A78BFA"/>
          <stop offset="100%" stopColor="#4C1D95"/>
        </radialGradient>
        <linearGradient id="sGemTop" x1="8" y1="5" x2="20" y2="12" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#EDE9FE"/>
          <stop offset="100%" stopColor="#8B5CF6"/>
        </linearGradient>
        <linearGradient id="sGemBot" x1="8" y1="12" x2="20" y2="23" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#8B5CF6"/>
          <stop offset="100%" stopColor="#4C1D95"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

const rarityClass = {
  common: 'border-edge bg-surfaceAlt text-fg',
  rare: 'border-ocean/50 bg-ocean/15 text-ocean-light',
  epic: 'border-gem/50 bg-gem/15 text-gem',
  legendary: 'border-accent/70 bg-accent/25 text-accent',
};

function BadgeMini({ badge }) {
  const initials = badge?.name
    ?.split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'HC';

  return (
    <div className="text-center">
      <div className={`mx-auto grid h-12 w-12 place-items-center overflow-hidden rounded-full border-2 ${rarityClass[badge?.rarity] || rarityClass.common}`}>
        {badge?.imageUrl ? (
          <img src={resolveAssetUrl(badge.imageUrl)} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="font-serif text-sm font-black">{initials}</span>
        )}
      </div>
      <p className="mt-1 truncate text-[10px] font-black text-fg/65">{badge?.name}</p>
    </div>
  );
}

function AvatarEditor({ player, onSaved }) {
  const [mode, setMode] = useState(null); // null | 'preset' | 'upload' | 'ai'
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiStep, setAiStep] = useState('idle'); // idle | uploading | generating | done
  const [error, setError] = useState('');
  const photoInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const avatarUrl = player.avatarUrl || '/avatars/avatar_1.png';

  const reset = () => {
    setMode(null);
    setPreview(null);
    setSelectedFile(null);
    setError('');
    setAiStep('idle');
  };

  const handlePresetSelect = async (url) => {
    setSaving(true);
    setError('');
    try {
      const res = await playerService.updateProfile({ avatarUrl: url });
      onSaved(res.data.data);
      reset();
    } catch {
      setError('Não foi possível guardar o avatar.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleUploadSave = async () => {
    if (!selectedFile) return;
    setSaving(true);
    setError('');
    try {
      const res = await playerService.uploadAvatar(selectedFile);
      onSaved(res.data.data);
      reset();
    } catch {
      setError('Não foi possível guardar o avatar.');
    } finally {
      setSaving(false);
    }
  };

  const handleAiPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleAiGenerate = async () => {
    if (!selectedFile) return;
    setAiStep('uploading');
    setError('');
    try {
      setAiStep('generating');
      const res = await playerService.generateAiAvatar(selectedFile, (e) => {
        if (e.loaded === e.total) setAiStep('generating');
      });
      onSaved(res.data.data);
      setAiStep('done');
      setTimeout(reset, 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível gerar o avatar. Tenta novamente.');
      setAiStep('idle');
    }
  };

  if (!mode) {
    return (
      <div className="mt-4 grid gap-3">
        <p className="text-center text-xs font-bold uppercase tracking-widest text-fg/50">Alterar avatar</p>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setMode('preset')}
            className="flex flex-col items-center gap-2 rounded-xl p-3 text-fg transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 100%)',
              border: '1.5px solid rgba(212,175,55,0.4)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <IconPreset />
            <span className="text-[11px] font-black tracking-wide">Predefinido</span>
          </button>
          <button
            type="button"
            onClick={() => { setMode('upload'); setTimeout(() => uploadInputRef.current?.click(), 50); }}
            className="flex flex-col items-center gap-2 rounded-xl p-3 text-fg transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(27,108,168,0.18) 0%, rgba(27,108,168,0.06) 100%)',
              border: '1.5px solid rgba(27,108,168,0.4)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <IconCamera />
            <span className="text-[11px] font-black tracking-wide">Foto</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('ai')}
            className="flex flex-col items-center gap-2 rounded-xl p-3 text-fg transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0.06) 100%)',
              border: '1.5px solid rgba(124,58,237,0.4)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <IconAI />
            <span className="text-[11px] font-black tracking-wide">Cartoon IA</span>
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'preset') {
    return (
      <div className="mt-4">
        <p className="mb-3 text-center text-xs font-bold uppercase tracking-widest text-fg/50">Escolhe um avatar</p>
        <div className="flex justify-center gap-4">
          {AVATAR_PRESETS.map((url) => (
            <button
              key={url}
              type="button"
              onClick={() => handlePresetSelect(url)}
              disabled={saving}
              className={`h-20 w-20 overflow-hidden rounded-full border-4 transition-all ${resolveAvatarUrl(avatarUrl) === resolveAvatarUrl(url) ? 'border-accent shadow-[0_0_0_3px_rgba(212,175,55,0.4)]' : 'border-edge hover:border-accent/60'}`}
            >
              <img src={resolveAvatarUrl(url)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
        {error && <p className="mt-2 text-center text-xs font-bold text-red-600">{error}</p>}
        <button type="button" onClick={reset} className="mt-3 w-full text-center text-xs font-bold text-fg/50 underline">
          Cancelar
        </button>
      </div>
    );
  }

  if (mode === 'upload') {
    return (
      <div className="mt-4">
        <input ref={uploadInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadSelect} />
        {!preview ? (
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-ocean/40 bg-ocean/5 py-6 text-center text-sm font-bold text-fg/60 hover:bg-ocean/10"
          >
            Toca para escolher uma foto
          </button>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <img src={preview} alt="" className="h-24 w-24 rounded-full border-4 border-accent object-cover shadow-lg" />
            <div className="flex gap-2">
              <button type="button" onClick={() => uploadInputRef.current?.click()} className="rounded-lg border border-edge px-3 py-1.5 text-xs font-bold text-fg/60">
                Trocar
              </button>
              <button
                type="button"
                onClick={handleUploadSave}
                disabled={saving}
                className="rounded-lg bg-ocean px-4 py-1.5 text-xs font-bold text-white disabled:opacity-60"
              >
                {saving ? 'A guardar...' : 'Usar esta foto'}
              </button>
            </div>
          </div>
        )}
        {error && <p className="mt-2 text-center text-xs font-bold text-red-600">{error}</p>}
        <button type="button" onClick={reset} className="mt-3 w-full text-center text-xs font-bold text-fg/50 underline">
          Cancelar
        </button>
      </div>
    );
  }

  if (mode === 'ai') {
    return (
      <div className="mt-4">
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handleAiPhotoSelect} />

        <div className="rounded-xl border border-gem/30 bg-gem/5 p-3 text-center">
          <p className="text-sm font-black text-fg">✨ Avatar Cartoon IA</p>
          <p className="mt-1 text-xs text-fg/60">
            Envia uma selfie e a IA transforma-te num herói de aventura personalizado.
          </p>
        </div>

        {aiStep === 'generating' ? (
          <div className="mt-4 flex flex-col items-center gap-3 py-4">
            <div className="relative h-16 w-16">
              <div className="absolute inset-0 animate-spin rounded-full border-4 border-gem/20 border-t-gem" />
              <span className="absolute inset-0 flex items-center justify-center text-2xl">✨</span>
            </div>
            <p className="text-center text-sm font-bold text-fg">A criar o teu avatar...</p>
            <p className="text-center text-xs text-fg/50">Pode demorar 20–40 segundos</p>
          </div>
        ) : aiStep === 'done' ? (
          <div className="mt-4 flex flex-col items-center gap-2 py-4">
            <span className="text-4xl">🎉</span>
            <p className="font-bold text-fg">Avatar criado com sucesso!</p>
          </div>
        ) : (
          <>
            {!preview ? (
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="mt-3 w-full rounded-xl border-2 border-dashed border-gem/40 bg-gem/5 py-6 text-center text-sm font-bold text-fg/60 hover:bg-gem/10"
              >
                Toca para escolher uma selfie
              </button>
            ) : (
              <div className="mt-3 flex flex-col items-center gap-3">
                <div className="relative">
                  <img src={preview} alt="" className="h-24 w-24 rounded-full border-4 border-gem/50 object-cover shadow-lg" />
                  <span className="absolute -right-1 -top-1 rounded-full bg-gem px-1.5 py-0.5 text-[10px] font-black text-white">FOTO</span>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => photoInputRef.current?.click()} className="rounded-lg border border-edge px-3 py-1.5 text-xs font-bold text-fg/60">
                    Trocar
                  </button>
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    className="rounded-lg bg-gradient-to-r from-gem to-purple-500 px-4 py-1.5 text-xs font-black text-white shadow-md"
                  >
                    ✨ Gerar avatar
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <p className="mt-2 text-center text-xs font-bold text-red-600">{error}</p>}
        {aiStep === 'idle' && (
          <button type="button" onClick={reset} className="mt-3 w-full text-center text-xs font-bold text-fg/50 underline">
            Cancelar
          </button>
        )}
      </div>
    );
  }

  return null;
}

function DisplayNameEditor({ player, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(player.displayName || player.username || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    const name = value.trim();
    if (!name || name.length < 2) { setError('Nome demasiado curto.'); return; }
    if (name === (player.displayName || player.username)) { setEditing(false); return; }
    setSaving(true);
    setError('');
    try {
      const res = await playerService.updateProfile({ displayName: name });
      onSaved(res.data.data);
      setEditing(false);
    } catch {
      setError('Não foi possível guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-center gap-2">
        <h2 className="font-serif text-xl font-black text-fg">{player.displayName || player.username || 'Aventureiro'}</h2>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-bold text-muted transition hover:text-muted"
          style={{ border: '1px solid rgba(255,255,255,0.15)' }}
        >
          Editar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 items-center">
      <div className="flex gap-2">
        <input
          autoFocus
          className="rounded-lg border border-edge bg-surfaceAlt px-3 py-1.5 font-serif text-sm font-bold text-fg outline-none focus:border-accent text-center"
          style={{ backdropFilter: 'blur(8px)' }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          maxLength={30}
        />
        <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-accent px-3 py-1.5 text-xs font-black text-white disabled:opacity-60">
          {saving ? '...' : 'Ok'}
        </button>
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-edge px-3 py-1.5 text-xs font-bold text-muted">
          ✕
        </button>
      </div>
      {error && <p className="text-xs font-bold text-red-400">{error}</p>}
    </div>
  );
}

const rarityLabel = { common: 'Comum', rare: 'Raro', epic: 'Épico', legendary: 'Lendário' };
const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };

export default function PlayerProfilePage() {
  const storedPlayer = useAuthStore((state) => state.player);
  const updatePlayer = useAuthStore((state) => state.updatePlayer);
  const { handleLogout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(storedPlayer);
  const [visits, setVisits] = useState([]);
  const [badges, setBadges] = useState([]);
  const [status, setStatus] = useState('');
  const [editingAvatar, setEditingAvatar] = useState(false);

  useEffect(() => {
    Promise.all([
      playerService.getProfile(),
      playerService.getVisitHistory(8),
      playerService.getBadges(),
    ])
      .then(([profileRes, visitsRes, badgesRes]) => {
        setProfile(profileRes.data.data);
        updatePlayer(profileRes.data.data);
        setVisits(visitsRes.data.data || []);
        const allBadges = badgesRes.data.data?.badges || [];
        const unlocked = allBadges
          .filter((b) => b.unlocked)
          .sort((a, b) => (rarityOrder[a.rarity] ?? 9) - (rarityOrder[b.rarity] ?? 9));
        setBadges(unlocked);
      })
      .catch((err) => setStatus(err.response?.data?.message || 'Não foi possível carregar o perfil.'));
  }, [updatePlayer]);

  const player = profile || storedPlayer || {};
  const avatarUrl = resolveAvatarUrl(player.avatarUrl);
  const { current: xpCurrent, needed: xpNeeded } = getXpProgress(player.xp || 0, player.level || 1);

  const handleProfileSaved = (updated) => {
    setProfile(updated);
    updatePlayer(updated);
    setEditingAvatar(false);
  };

  const levelLabel = ['I','II','III','IV','V','VI','VII','VIII','IX','X'][((player.level || 1) - 1)] || `${player.level}`;

  return (
    <main className="h-full overflow-y-auto bg-background pb-4">
      {status && <div className="px-4 pt-4"><ParchmentCard className="text-fg text-sm">{status}</ParchmentCard></div>}

      {/* Hero section — avatar centralizado */}
      <div
        className="relative flex flex-col items-center px-4 pb-6 pt-6"
        style={{
          background: 'linear-gradient(180deg, rgba(212,175,55,0.08) 0%, transparent 100%)',
        }}
      >
        {/* título + botão definições */}
        <div className="mb-5 flex w-full items-center justify-between px-0">
          <h1 className="font-serif text-2xl font-black tracking-wide text-accent">Perfil</h1>
          <button
            type="button"
            onClick={() => navigate('/game/settings')}
            aria-label="Definições do jogo"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-edge bg-surfaceAlt text-muted hover:text-fg transition-colors active:scale-95"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </div>

        {/* avatar grande centralizado */}
        <div className="relative">
          {/* anel decorativo externo */}
          <div
            className="absolute -inset-2 rounded-full"
            style={{
              background: 'conic-gradient(from 0deg, rgba(212,175,55,0.6), rgba(255,215,0,0.9), rgba(184,134,11,0.4), rgba(255,215,0,0.9), rgba(212,175,55,0.6))',
              filter: 'blur(2px)',
            }}
          />
          <div
            className="absolute -inset-1 rounded-full"
            style={{ background: 'rgb(var(--hc-bg))' }}
          />
          <button
            type="button"
            onClick={() => setEditingAvatar((v) => !v)}
            className="group relative block overflow-hidden rounded-full"
            style={{
              width: 112,
              height: 112,
              border: '3px solid rgb(var(--hc-accent))',
              boxShadow: '0 0 0 1px rgb(var(--hc-accent) / 0.3), 0 0 24px rgb(var(--hc-accent) / 0.35), 0 8px 32px rgb(var(--hc-bg) / 0.5)',
            }}
          >
            <img src={avatarUrl} alt="Avatar do jogador" className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-end justify-center bg-black/0 pb-2 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
              <span
                className="rounded-full px-2.5 py-0.5 text-[9px] font-black text-white"
                style={{ background: 'linear-gradient(135deg, rgb(var(--hc-accent-neon)), rgb(var(--hc-accent)))' }}
              >
                EDITAR
              </span>
            </div>
          </button>

          {/* badge de nível */}
          <div
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex h-7 min-w-[28px] items-center justify-center rounded-full px-2 font-urban text-[11px] font-black text-white"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--hc-accent-neon)) 0%, rgb(var(--hc-accent)) 100%)',
              border: '2px solid rgb(var(--hc-bg))',
              boxShadow: '0 2px 8px rgb(var(--hc-bg) / 0.5)',
            }}
          >
            {levelLabel}
          </div>
        </div>

        {/* nome e username */}
        <div className="mt-4 w-full text-center">
          <DisplayNameEditor player={player} onSaved={handleProfileSaved} />
          <p className="mt-1 text-xs font-bold text-muted">@{player.username}</p>
        </div>

        {/* medalhas em destaque (mini) */}
        {player.badges?.some((pb) => pb.badge?.imageUrl) && (
          <div className="mt-3 flex items-center gap-2">
            {player.badges.slice(0, 3).map((pb) => (
              pb.badge?.imageUrl && (
                <img
                  key={pb.id}
                  src={resolveAssetUrl(pb.badge.imageUrl)}
                  alt={pb.badge.name}
                  title={pb.badge.name}
                  className="h-8 w-8 rounded-full object-cover"
                  style={{ border: '2px solid rgba(212,175,55,0.5)', boxShadow: '0 0 8px rgba(212,175,55,0.2)' }}
                />
              )
            ))}
          </div>
        )}

        {/* Editor de avatar */}
        {editingAvatar && (
          <div className="mt-4 w-full">
            <ParchmentCard className="text-fg">
              <AvatarEditor
                player={player}
                onSaved={(updated) => { handleProfileSaved(updated); setEditingAvatar(false); }}
              />
            </ParchmentCard>
          </div>
        )}
      </div>

      {/* Card stats + XP */}
      <div className="px-4">
        <ParchmentCard className="text-fg">
          {/* XP */}
          <div>
            <div className="mb-1.5 flex justify-between text-xs font-bold text-fg/60">
              <span>{xpCurrent.toLocaleString('pt-PT')} XP</span>
              <span>{xpNeeded.toLocaleString('pt-PT')} XP · Nível {(player.level || 1) + 1}</span>
            </div>
            <ProgressBar value={xpCurrent} max={xpNeeded} color="blue" />
          </div>

          {/* Estatísticas */}
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            <div
              className="rounded-xl py-2.5 px-1"
              style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', border: '1px solid rgba(212,175,55,0.25)' }}
            >
              <StatCoinIcon />
              <strong className="block font-serif text-base font-black text-accent leading-none">{(player.coins || 0).toLocaleString('pt-PT')}</strong>
              <span className="mt-0.5 block text-[9px] font-black uppercase tracking-wide text-fg/50">Moedas</span>
            </div>
            <div
              className="rounded-xl py-2.5 px-1"
              style={{ background: 'linear-gradient(135deg, rgba(27,108,168,0.15) 0%, rgba(27,108,168,0.05) 100%)', border: '1px solid rgba(27,108,168,0.25)' }}
            >
              <StatPOIIcon />
              <strong className="block font-serif text-base font-black text-ocean leading-none">{player.totalPoisVisited || 0}</strong>
              <span className="mt-0.5 block text-[9px] font-black uppercase tracking-wide text-fg/50">POIs</span>
            </div>
            <div
              className="rounded-xl py-2.5 px-1"
              style={{ background: 'linear-gradient(135deg, rgba(21,128,61,0.15) 0%, rgba(21,128,61,0.05) 100%)', border: '1px solid rgba(21,128,61,0.25)' }}
            >
              <StatMissionIcon />
              <strong className="block font-serif text-base font-black text-forest leading-none">{player.totalMissionsCompleted || 0}</strong>
              <span className="mt-0.5 block text-[9px] font-black uppercase tracking-wide text-fg/50">Missões</span>
            </div>
            <div
              className="rounded-xl py-2.5 px-1"
              style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(124,58,237,0.05) 100%)', border: '1px solid rgba(124,58,237,0.25)' }}
            >
              <StatGemIcon />
              <strong className="block font-serif text-base font-black text-gem leading-none">{player.gems || 0}</strong>
              <span className="mt-0.5 block text-[9px] font-black uppercase tracking-wide text-fg/50">Gemas</span>
            </div>
          </div>
        </ParchmentCard>
      </div>

      {/* Medalhas conquistadas */}
      <section className="mt-5 px-4">
        <h2 className="font-serif text-lg font-black text-accent">Medalhas conquistadas</h2>
        {badges.length === 0 ? (
          <ParchmentCard className="mt-3 text-fg text-sm">
            Ainda não conquistaste nenhuma medalha. Explora a cidade!
          </ParchmentCard>
        ) : (
          <ParchmentCard className="mt-3">
            <div className="grid grid-cols-3 gap-4">
              {badges.map((badge) => (
                <div key={badge.id} className="flex flex-col items-center gap-1.5 text-center">
                  <div className={`grid h-16 w-16 place-items-center overflow-hidden rounded-full border-2 shadow-md ${rarityClass[badge.rarity] || rarityClass.common}`}>
                    {badge.imageUrl ? (
                      <img src={resolveAssetUrl(badge.imageUrl)} alt={badge.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="font-serif text-lg font-black">
                        {badge.name?.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase() || 'HC'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-black text-fg leading-tight">{badge.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wide ${rarityClass[badge.rarity] || rarityClass.common}`}>
                    {rarityLabel[badge.rarity] || badge.rarity}
                  </span>
                  {badge.unlockedAt && (
                    <p className="text-[9px] text-fg/45">
                      {new Date(badge.unlockedAt).toLocaleDateString('pt-PT')}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-4 text-center text-xs font-bold text-fg/50">
              {badges.length} medalha{badges.length !== 1 ? 's' : ''} conquistada{badges.length !== 1 ? 's' : ''}
            </p>
          </ParchmentCard>
        )}
      </section>

      {/* Histórico */}
      <section className="mt-5 px-4">
        <h2 className="font-serif text-lg font-black text-accent">Histórico de visitas</h2>
        <div className="mt-3 grid gap-3">
          {visits.length === 0 && (
            <ParchmentCard className="text-fg text-sm">Ainda não há visitas registadas.</ParchmentCard>
          )}
          {visits.map((visit) => (
            <ParchmentCard key={visit.id} className="text-fg">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border border-edge bg-surfaceAlt">
                  {visit.poi?.imageUrl ? (
                    <img src={resolveAssetUrl(visit.poi.imageUrl)} alt={visit.poi.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl">⚓</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-serif font-bold">{visit.poi?.name || 'Local visitado'}</h3>
                  <p className="text-xs text-fg/60">
                    {visit.poi?.zone?.name || 'Zona'} · {new Date(visit.visitedAt).toLocaleDateString('pt-PT')}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right text-xs font-bold">
                  <div className="text-accent">🪙 +{visit.coinsEarned}</div>
                  <div className="text-xp">⭐ +{visit.xpEarned}</div>
                </div>
              </div>
            </ParchmentCard>
          ))}
        </div>
      </section>

      <div className="mt-6 px-4">
        <Button fullWidth variant="danger" onClick={handleLogout}>
          Sair
        </Button>
      </div>
    </main>
  );
}
